import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, EyeOff, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/vangard/GoogleAuthButton";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/i18n/LanguageContext";
import LangSwitcher from "@/components/LangSwitcher";
import VMark from "@/components/vangard/VMark";
import Seo from "@/components/Seo";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register, loginWithGoogleCredential } = useAuth();
  const { t } = useTranslation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t("vc.auth.pw-mismatch"));
      return;
    }
    if (password.length < 6) {
      toast.error(t("vc.auth.pw-min"));
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/account");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("vc.auth.register-fail"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid lg:grid-cols-2" data-testid="register-page">
      <Seo title={t("vc.seo.register.title")} description={t("vc.seo.register.desc")} noindex />
      <aside className="relative hidden lg:flex flex-col justify-between p-12 border-r border-white/[0.06] overflow-hidden">
        <VMark position="top-right" size={720} opacity={0.06} />
        <Link to="/" className="font-serif text-[22px] relative z-10">
          Vangard<span className="text-neutral-400">Club</span>
        </Link>
        <div className="relative z-10 max-w-md">
          <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-400">{t("vc.auth.open-membership")}</div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mt-6">
            {t("vc.auth.create-title")}
          </h1>
          <p className="mt-6 text-neutral-400 font-light leading-relaxed">
            {t("vc.auth.create-line")}
          </p>
        </div>
        <div className="relative z-10 text-[10px] tracking-[0.35em] uppercase text-neutral-600">
          {t("vc.auth.quote-app")}
        </div>
      </aside>

      <main className="relative flex flex-col">
        <div className="flex items-center justify-between px-6 lg:px-12 py-6 border-b border-white/[0.06]">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-neutral-400 hover:text-white transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft size={16} />
            {t("common.back")}
          </button>
          <LangSwitcher />
        </div>

        <div className="flex-1 flex items-center px-6 lg:px-12 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md mx-auto"
          >
            <div className="text-[11px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.auth.new-member")}</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-3 leading-[1.05]">
              {t("vc.auth.join-vangard")}.
            </h2>

            <form onSubmit={onSubmit} className="mt-7 space-y-4">
              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.auth.full-name")}</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-2 bg-transparent border-0 border-b border-white/15 text-white text-base font-light py-2.5 focus:border-white/60 focus:outline-none transition-colors placeholder:text-neutral-600"
                  placeholder="John Doe"
                  data-testid="register-name-input"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.auth.email")}</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full mt-2 bg-transparent border-0 border-b border-white/15 text-white text-base font-light py-2.5 focus:border-white/60 focus:outline-none transition-colors placeholder:text-neutral-600"
                  placeholder="you@example.com"
                  data-testid="register-email-input"
                />
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.auth.password")}</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full mt-2 bg-transparent border-0 border-b border-white/15 text-white text-base font-light py-2.5 pr-8 focus:border-white/60 focus:outline-none transition-colors placeholder:text-neutral-600"
                    placeholder="••••••••"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-0 top-1/2 translate-y-1 text-neutral-500 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">{t("vc.auth.confirm-password")}</label>
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full mt-2 bg-transparent border-0 border-b border-white/15 text-white text-base font-light py-2.5 focus:border-white/60 focus:outline-none transition-colors placeholder:text-neutral-600"
                  placeholder="••••••••"
                  data-testid="register-confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-2 inline-flex items-center justify-center gap-3 bg-white text-black px-8 py-3.5 rounded-full text-[12px] tracking-[0.25em] uppercase font-medium hover:bg-neutral-200 transition-colors disabled:opacity-50"
                data-testid="register-submit-btn"
              >
                {loading ? t("vc.auth.creating") : t("vc.auth.create-account")}
                <ArrowRight size={14} />
              </button>
            </form>

            <div className="relative my-6 flex items-center gap-4">
              <span className="h-px flex-1 bg-white/10" />
              <span className="text-[11px] tracking-[0.35em] uppercase text-neutral-500">{t("vc.auth.or")}</span>
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <GoogleAuthButton
              label={t("vc.auth.continue-google")}
              onCredential={(cred) => loginWithGoogleCredential(cred).then(() => navigate("/account")).catch(() => toast.error(t("vc.auth.google-fail")))}
              onError={() => toast.error(t("vc.auth.google-fail"))}
              testid="register-google-btn"
            />

            <p className="text-center mt-6 text-sm text-neutral-400">
              {t("vc.auth.have-account")}{" "}
              <Link
                to="/login"
                className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors"
                data-testid="signin-link"
              >
                {t("vc.auth.sign-in")}
              </Link>
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
